<template>
    <USlideover prevent-close :model-value="open" @update:model-value="$emit('update:open', $event)" :ui="{ width: 'max-w-xl' }">
        <UCard>
            <template #header>
                <div class="flex items-center justify-between">
                    <h3 class="text-base font-medium leading-6 text-gray-800">Import Credentials</h3>
                </div>
            </template>

            <div v-if="loading" class="h-full flex flex-col items-start justify-center space-y-2 p-8 sm:p-24">
                <div class="w-full flex items-center justify-between">
                    <div class="flex items-center space-x-2.5">
                        <UIcon name="i-ph-circle-notch-bold" class="size-4 text-gray-600 animate-spin" />
                        <p class="text-sm font-medium text-gray-700">Your file is being imported...</p>
                    </div>
                    <span class="text-xs text-gray-400">{{ importProgress }}%</span>
                </div>
                <UProgress color="primary" :value="importProgress" />
                <div class="flex items-center space-x-2 !mt-3">
                    <UIcon name="i-ph-warning-duotone" class="size-4 text-orange-400" />
                    <p class="text-xs text-orange-500">Please do not close this window!</p>
                </div>
            </div>
            <div v-else-if="completed" class="h-full flex flex-col items-start justify-center space-y-4 p-8 sm:p-24">
                <div class="w-full space-y-2">
                    <h2 class="text-lg font-medium text-gray-700">
                        Import completed successfully!
                    </h2>
                    <div class="flex items-center space-x-2.5">
                        <UIcon name="i-ph-check-circle-duotone" class="size-5 text-green-500" />
                        <p class="text-sm font-medium text-gray-600">
                            <span class="font-mono font-semibold">{{ importState.total - importState.existing - importState.expired }}</span> credentials inserted
                        </p>
                    </div>
                    <div class="flex items-center space-x-2.5">
                        <UIcon name="i-ph-x-circle-duotone" class="size-5 text-red-500" />
                        <p class="text-sm font-medium text-gray-600">
                            <span class="font-mono font-semibold">{{ importState.existing }}</span> credentials skipped (already existing)
                        </p>
                    </div>
                    <div v-if="importState.expired" class="flex items-center space-x-2.5">
                        <UIcon name="i-ph-clock-duotone" class="size-5 text-orange-500" />
                        <p class="text-sm font-medium text-gray-600">
                            <span class="font-mono font-semibold">{{ importState.expired }}</span> credentials skipped (expired)
                        </p>
                    </div>
                </div>

                <div v-if="importState.expiredRemoved || importState.duplicatesRemoved" class="w-full space-y-2">
                    <div class="w-full h-px bg-gray-200 !my-4"></div>
                    <h2 class="text-base font-medium text-gray-700">
                        Cleanup
                    </h2>
                    <div v-if="importState.expiredRemoved" class="flex items-center space-x-2.5">
                        <UIcon name="i-ph-trash-duotone" class="size-5 text-purple-500" />
                        <p class="text-sm font-medium text-gray-600">
                            <span class="font-mono font-semibold">{{ importState.expiredRemoved }}</span> unused and expired credentials removed
                        </p>
                    </div>
                    <div v-if="importState.duplicatesRemoved" class="flex items-center space-x-2.5">
                        <UIcon name="i-ph-copy-duotone" class="size-5 text-blue-500" />
                        <p class="text-sm font-medium text-gray-600">
                            <span class="font-mono font-semibold">{{ importState.duplicatesRemoved }}</span> duplicates removed
                        </p>
                    </div>
                </div>
            </div>
            <div v-else class="space-y-6">
                <UFormGroup name="partner_id" label="Partner" required>
                    <UInputMenu
                        v-model="partner.selected"
                        @update:model-value="state.partner_id = $event.id"
                        :loading="partner.searching"
                        :search="searchPartner"
                        placeholder="Choose a partner..."
                        option-attribute="name"
                        size="xl"
                        trailing
                        by="id"
                    />
                </UFormGroup>
                <template v-if="partner.selected">
                    <div>
                        <p class="text-sm text-gray-500">
                            Upload a <b>CSV</b> file with the table headers as the first row, making sure they match exactly as shown below:
                        </p>
                        <div class="mt-1 mb-3 flex flex-wrap gap-1">
                            <UTooltip
                                v-for="field in subscriptionFields"
                                :key="field.key"
                                :text="field.required ? 'Required' : 'Optional'"
                            >
                                <UBadge
                                    :label="field.key"
                                    :color="field.required ? 'orange' : 'blue'"
                                    variant="subtle"
                                    class="font-mono font-semibold"
                                />
                            </UTooltip>
                        </div>
                        <p class="text-sm text-gray-500">
                            Tip: The <span class="font-mono font-semibold text-xs bg-gray-100 p-1 rounded-md">expiry_date</span> column
                            should be formatted as YYYY-MM-DD
                        </p>
                    </div>
                    <div class="rounded-md border border-dashed border-strong border-gray-300">
                        <label v-if="!state.file" class="h-36 flex items-center justify-center space-x-1 cursor-pointer">
                            <UIcon name="i-ph-file-text-duotone" class="size-4" />
                            <p class="text-sm">Choose a <span class="text-primary">file</span> to import</p>
                            <input @change="onChooseFile" ref="fileInput" type="file" accept="text/csv" class="sr-only" />
                        </label>
                        <div v-else class="h-28 flex flex-col items-center justify-center space-y-3">
                            <div class="flex items-center space-x-2">
                                <UIcon name="i-ph-file-text-duotone" class="size-4" />
                                <span class="text-sm text-gray-600">{{ state.file.name }}</span>
                            </div>
                            <UButton @click="reset()" size="xs" color="gray"> Remove File </UButton>
                        </div>
                    </div>

                    <UAlert
                        v-if="state.errors?.length"
                        title="There are errors in the selected file"
                        icon="i-ph-warning-duotone"
                        color="red"
                        variant="subtle"
                    >
                        <template #description>
                            <ul class="list-disc pl-6 pt-1">
                                <li v-for="error in state.errors" class="text-xs">
                                    <strong v-if="error.row !== undefined"
                                        >Row <span class="font-mono">{{ error.row + 2 }}</span
                                        >:</strong
                                    >
                                    {{ error.message }}
                                </li>
                            </ul>
                        </template>
                    </UAlert>
                    <div v-else-if="state.data.length">
                        <h3 class="mb-0.5 text-sm font-medium text-gray-600">Preview data</h3>
                        <p class="mb-3 text-sm text-gray-500">
                            Here is a preview of the data that will be imported: a total of {{ state.data.length }} credential(s).
                        </p>
                        <UTable
                            class="border rounded-md min-h-[300px]"
                            :rows="state.data"
                            :columns="previewTableColumns"
                            :ui="{ 
                                th: {
                                    base: 'first:border-0 border-l bg-gray-50',
                                    padding: 'first:pl-3 px-3 py-1.5',
                                    size: 'lowercase'
                                },
                                td: { 
                                    base: 'first:border-l-0 border-l border-b',
                                    padding: 'first:pl-3 px-3 py-2',
                                    size: 'text-sm max-w-[200px] overflow-hidden truncate'
                                }
                            }"
                        />
                    </div>
                </template>
            </div>

            <template #footer v-if="completed || !loading">
                <div class="flex items-center justify-end space-x-2">
                    <UButton v-if="completed" @click="dismiss(true)" size="lg" color="white"> Close </UButton>
                    <template v-else-if="!loading">
                        <UButton @click="dismiss()" size="lg" color="white"> Cancel </UButton>
                        <UButton @click="onSubmit()" size="lg" :disabled="!canImport" color="black"> Import data </UButton>
                    </template>
                </div>
            </template>
        </UCard>
    </USlideover>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import Papa from 'papaparse'
import { sha256 } from 'js-sha256'
import type { Partner } from '~/types/partner'
import type { Credential } from '~/types/credential'

const emit = defineEmits<{
    'update:open': [boolean]
    'reload': [boolean]
}>()

const props = defineProps<{
    open: boolean
}>()

const toast = useToast()
const client = useSupabaseClient()

const loading = ref<boolean>(false)
const completed = ref<boolean>(false)
const fileInput = ref<HTMLInputElement>()

const state = ref<{
    partner_id?: string
    file?: File
    data: any[]
    errors: Papa.ParseError[]
    meta?: Papa.ParseMeta
}>({
    partner_id: undefined,
    file: undefined,
    data: [],
    errors: [],
})

const partner = ref<{
    searching: boolean
    selected?: Partial<Partner>
}>({
    searching: false,
})

const importState = ref<{
    counter: number
    total: number
    existing: number
    expired: number
    expiredRemoved: number
    duplicatesRemoved: number
    errors: Partial<Papa.ParseError>[]
}>({
    counter: 0,
    total: 0,
    existing: 0,
    expired: 0,
    expiredRemoved: 0,
    duplicatesRemoved: 0,
    errors: []
})

const subscriptionFields = computed(() => {
    return [
        ...partner.value.selected?.metadata?.subscription_fields || [],
        { key: 'expiry_date', label: 'Expiry Date', required: true },
    ]
})

const previewTableColumns = computed(() => subscriptionFields.value.map((field) => { 
    return { 
        key: field.key, 
        label: field.key + (field.required ? '*' : ''),
        class: 'w-16 overflow-hidden truncate'
    }
}))

const importProgress = computed(() => {
    return importState.value.total
        ? Math.round(importState.value.counter * 100 / importState.value.total)
        : 0
})

const canImport = computed(() => {
    return state.value?.partner_id && state.value?.file && state.value.data.length && !state.value.errors.length
})

watch(() => props.open, (open) => {
    if (open) {
        reset(true)
    }
})

async function searchPartner(q: string) {
    partner.value.searching = true

    const { data, error } = await client
        .from('partners')
        .select('id, name, metadata')
        .is('deleted_at', null)
        .eq('metadata ->> is_public', true)
        .ilike('name', `%${q}%`)

    if (error) {
        return []
    }

    partner.value.searching = false
    return data
}

function onChooseFile() {
    if (fileInput.value && fileInput.value.files?.length) {
        state.value.file = fileInput.value.files[0]
        if (state.value.file.size > 5000000) {
            toast.add({
                title: 'File size cannot be larger than 5MB',
                color: 'red',
                icon: 'i-ph-warning-duotone',
            })
            return
        }

        Papa.parse(state.value.file, {
            header: true,
            dynamicTyping: false,
            skipEmptyLines: true,
            complete: async (results: Papa.ParseResult<any>) => {
                importState.value.total = results.data.length
            },
        })

        Papa.parse(state.value.file, {
            header: true,
            dynamicTyping: false,
            skipEmptyLines: true,
            preview: 20,
            complete: async (results: Papa.ParseResult<any>) => {
                const missingRequiredFields = subscriptionFields.value
                    .filter((field) => field.required && !results.meta.fields?.includes(field.key))
                    .map((field) => `'${field.key}'`)

                if (missingRequiredFields?.length) {
                    state.value.errors.push({
                        row: -1,
                        message: 'Header missing required fields: ' + missingRequiredFields.join(', '),
                    } as Papa.ParseError)
                }

                if (results.errors) {
                    state.value.errors.push(...results.errors)
                }

                if (results.data) {
                    state.value.data = results.data
                }
            },
        })
    }
}

async function cleanup(partnerId: string) {
    const { data: expiredCredentials } = await client
        .from('credentials')
        .select('id')
        .eq('partner_id', partnerId)
        .is('invalidated_at', null)
        .lt('expires_at', dayjs().format('YYYY-MM-DD'))

    if (expiredCredentials?.length) {
        await client
            .from('credentials')
            .delete()
            .in('id', expiredCredentials.map(c => c.id))

        importState.value.expiredRemoved = expiredCredentials.length
    }

    const requiredFieldKeys = subscriptionFields.value
        .filter(f => f.required && f.key !== 'expiry_date')
        .map(f => f.key)
        .sort()

    const { data: credentials } = await client
        .from('credentials')
        .select('id, number, fields')
        .eq('partner_id', partnerId)
        .is('invalidated_at', null)
        .gte('expires_at', dayjs().format('YYYY-MM-DD'))

    if (!credentials?.length) return

    const duplicateGroups = new Map<string, typeof credentials>()

    credentials.forEach((credential) => {
        const key = requiredFieldKeys
            .map(fieldKey => normalizeFieldValue(fieldKey, credential.fields[fieldKey]))
            .join('|')

        if (!duplicateGroups.has(key)) {
            duplicateGroups.set(key, [])
        }
        duplicateGroups.get(key)!.push(credential)
    })

    const toRemove: string[] = []

    duplicateGroups.forEach((group) => {
        if (group.length <= 1) return
        group.sort((a, b) => b.number - a.number)
        toRemove.push(...group.slice(1).map(c => c.id))
    })

    if (toRemove.length) {
        await client
            .from('credentials')
            .delete()
            .in('id', toRemove)

        importState.value.duplicatesRemoved = toRemove.length
    }
}

// policy_holder is the canonical uppercase field everywhere (customers, subscriptions,
// renewals). exclusion is free-text and stored with its original casing for readable
// display. All other credential fields are lowercased for case-insensitive matching.
// Keep this used consistently across stored fields AND match keys so dedup stays aligned.
// (exclusion is an optional field, so it never participates in dedup/hash keys.)
function normalizeFieldValue(key: string, value: unknown): string {
    const trimmed = ((value || '') as string).toString().trim()
    if (key === 'policy_holder') return trimmed.toUpperCase()
    if (key === 'exclusion') return trimmed
    return trimmed.toLowerCase()
}

async function onSubmit() {
    if (state.value.file && partner.value.selected?.id) {
        loading.value = true

        const selectedPartnerId = partner.value.selected.id
        const requiredFieldKeys = subscriptionFields.value
            .filter(f => f.required && f.key !== 'expiry_date')
            .map(f => f.key)
            .sort()

        const { data: existingCredentials } = await client
            .from('credentials')
            .select('id, fields, expires_at')
            .eq('partner_id', selectedPartnerId)
            .gte('expires_at', dayjs().format('YYYY-MM-DD'))
            .is('invalidated_at', null)

        const existingCredentialsMap = new Map<string, any[]>()
        existingCredentials?.forEach(cred => {
            const key = requiredFieldKeys
                .map(fieldKey => normalizeFieldValue(fieldKey, cred.fields[fieldKey]))
                .join('|')
            const group = existingCredentialsMap.get(key) ?? []
            group.push(cred)
            existingCredentialsMap.set(key, group)
        })

        Papa.parse(state.value.file, {
            header: true,
            dynamicTyping: false,
            skipEmptyLines: true,
            chunkSize: 1024 * 16,
            chunk: async (results: Papa.ParseStepResult<any>, parser: Papa.Parser) => {
                parser.pause()

                const importData: Credential[] = []
                const toInvalidate: string[] = []

                for (const row of results.data) {
                    importState.value.counter++

                    const expiryDate = dayjs(row.expiry_date)

                    if (!expiryDate.isValid()) {
                        importState.value.errors.push({ row: importState.value.counter, message: 'Invalid expiry date: ' + row.expiry_date })
                        continue
                    }

                    if (expiryDate.isBefore(dayjs(), 'day')) {
                        importState.value.expired++
                        continue
                    }

                    const fields = subscriptionFields.value
                        .filter((field) => field.key !== 'expiry_date')
                        .reduce<{ [key: string]: string }>((acc, field) => {
                            acc[field.key] = normalizeFieldValue(field.key, row[field.key])
                            return acc
                        }, {})

                    const requiredFieldsData = requiredFieldKeys.reduce<{ [key: string]: string }>((acc, key) => {
                        acc[key] = normalizeFieldValue(key, row[key])
                        return acc
                    }, {})

                    const normalizedExpiryDate = expiryDate.format('YYYY-MM-DD')

                    const lookupKey = requiredFieldKeys
                        .map(key => requiredFieldsData[key])
                        .join('|')

                    const matches = existingCredentialsMap.get(lookupKey)

                    if (matches?.length) {
                        if (matches.some(match => dayjs(match.expires_at).isSame(expiryDate, 'day'))) {
                            importState.value.existing++
                            continue
                        }

                        toInvalidate.push(...matches.filter(match => match.id).map(match => match.id))
                    }

                    const hashObject: Record<string, string> = {
                        partner_id: selectedPartnerId,
                        expires_at: normalizedExpiryDate,
                    }

                    requiredFieldKeys.forEach(key => {
                        hashObject[key] = requiredFieldsData[key]
                    })

                    const hash = sha256(JSON.stringify(hashObject))

                    importData.push({
                        partner_id: selectedPartnerId,
                        expires_at: normalizedExpiryDate,
                        fields,
                        hash,
                    })

                    const lookupGroup = existingCredentialsMap.get(lookupKey) ?? []
                    lookupGroup.push({ fields: requiredFieldsData, expires_at: normalizedExpiryDate, id: null })
                    existingCredentialsMap.set(lookupKey, lookupGroup)
                }

                if (importData.length > 0 || toInvalidate.length > 0) {
                    const uniqueImportData = Array.from(
                        new Map(importData.map(item => [item.hash, item])).values()
                    )

                    await client.rpc('import_credentials', {
                        p_invalidate_ids: toInvalidate,
                        p_rows: uniqueImportData,
                    } as never)
                }

                parser.resume()
            },
            complete: async () => {
                await cleanup(selectedPartnerId)
                loading.value = false
                completed.value = true
            },
        })
    }
}

function dismiss(reload: boolean = false) {
    emit('update:open', false)

    if (reload) {
        emit('reload', true)
    }
}

function reset(resetPartner?: boolean) {
    loading.value = false
    completed.value = false
    
    if (resetPartner) {
        partner.value = {
            searching: false,
            selected: undefined
        }
    }

    state.value = {
        partner_id: resetPartner ? undefined : partner.value.selected?.id,
        file: undefined,
        data: [],
        errors: [],
        meta: undefined
    }

    importState.value = {
        counter: 0,
        total: 0,
        existing: 0,
        expired: 0,
        expiredRemoved: 0,
        duplicatesRemoved: 0,
        errors: []
    }
}
</script>
